<template>
  <div
    v-if="widget"
    class="hbt"
  >
    <div class="hbt-sec-head">
      <span class="hbt-h2">{{ widget.param.title || 'Тренировка' }}</span>
      <span class="hbt-kicker">{{ widget.param.subtitle || 'training' }}</span>
      <span
        class="hbt-mut"
        style="margin-left: auto;"
      >{{ pct }}%</span>
    </div>

    <div
      v-for="c in checkHabits"
      :key="`c${c.id}`"
      class="hbt-row"
    >
      <button
        type="button"
        class="hbt-check"
        :class="isDone(c) ? 'hbt-check-done' : ''"
        :aria-pressed="isDone(c)"
        :disabled="busy"
        @click="toggleCheck(c)"
      >{{ isDone(c) ? '✓' : '' }}</button>
      <div style="flex: 1;">
        <div class="hbt-h3">
          {{ c.name }}
        </div>
        <div class="hbt-mut">
          {{ c.en }}
        </div>
      </div>
      <span
        class="hbt-tag"
        :style="tagStyle(c)"
      >{{ isDone(c) ? 'Сделано' : 'Не сделано' }}</span>
    </div>

    <div
      v-for="ex in countHabits"
      :key="`e${ex.id}`"
      class="hbt-ex"
    >
      <div
        class="d-flex align-items-start"
        style="gap: 12px;"
      >
        <div style="flex: 1;">
          <div class="hbt-h3">
            {{ ex.name }}
          </div>
          <div class="hbt-mut">
            {{ ex.en }} · подходов: {{ setsOf(ex).length }}
          </div>
        </div>
        <div style="text-align: right;">
          <div
            class="d-flex align-items-baseline justify-content-end"
            style="gap: 4px;"
          >
            <span
              class="hbt-num"
              style="font-size: 28px;"
              :style="{ color: totalOf(ex) >= planOf(ex) && planOf(ex) > 0 ? '#ec3013' : '#201e1d' }"
            >{{ totalOf(ex) }}</span>
            <span class="hbt-mut">/</span>
            <input
              type="number"
              class="hbt-input hbt-plan-input"
              :value="planOf(ex)"
              aria-label="План на день"
              @change="onPlan(ex, $event)"
            >
          </div>
          <div
            class="hbt-kicker-mut"
            style="font-size: 10px; margin-top: 4px;"
          >
            план на сегодня
          </div>
        </div>
      </div>
      <div
        class="hbt-bar"
        style="height: 8px; margin: 10px 0;"
      >
        <div
          class="hbt-bar-fill"
          :style="{ width: `${pctOf(ex)}%` }"
        />
      </div>
      <div
        class="d-flex flex-wrap align-items-center"
        style="gap: 8px;"
      >
        <button
          v-for="s in steps"
          :key="s"
          type="button"
          class="hbt-btn"
          :disabled="busy"
          style="min-width: 52px;"
          @click="addSet(ex, s)"
        >+{{ s }}</button>
        <span
          class="hbt-mut"
          style="margin-left: auto;"
        >{{ setsText(ex) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import useCubus from '@/cubus/services/useCubus'
import {
  HBT_REFRESH, hbtLoadHabits, hbtLoadLogs, hbtDayCompletion, hbtDayKey,
  hbtAddLog, hbtDeleteTodayLogs, hbtSavePlan,
} from '@/cubus/components/dashboard/habits/habitsCore'

export default {
  name: 'CbsWidgetHabitTraining',
  props: {
    widget: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      habits: [],
      logs: [],
      busy: false,
    }
  },
  computed: {
    checkHabits() {
      return this.habits.filter(h => h.type === 'check')
    },
    countHabits() {
      return this.habits.filter(h => h.type === 'count')
    },
    steps() {
      const s = this.widget.param.steps
      return Array.isArray(s) && s.length ? s.map(Number) : [1, 5, 10]
    },
    todayKey() {
      return hbtDayKey(new Date())
    },
    completion() {
      return hbtDayCompletion(this.habits, this.logs, this.todayKey)
    },
    pct() {
      return this.completion.pct
    },
  },
  created() {
    this.load()
    this.$root.$on(HBT_REFRESH, this.reload)
  },
  beforeDestroy() {
    this.$root.$off(HBT_REFRESH, this.reload)
  },
  methods: {
    async load() {
      this.habits = await hbtLoadHabits(this, this.widget.param)
      this.logs = await hbtLoadLogs(this, this.widget.param, 1)
    },
    async reload() {
      this.logs = await hbtLoadLogs(this, this.widget.param, 1)
    },
    setsOf(h) {
      return this.logs
        .filter(l => l.habitId === h.id && l.day === this.todayKey)
        .sort((a, b) => a.date - b.date)
    },
    totalOf(h) {
      return this.setsOf(h).reduce((a, l) => a + l.reps, 0)
    },
    planOf(h) {
      return h.plan || 0
    },
    pctOf(h) {
      const plan = this.planOf(h)
      if (plan <= 0) return 0
      return Math.min(100, Math.round((this.totalOf(h) / plan) * 100))
    },
    isDone(h) {
      return this.totalOf(h) > 0
    },
    tagStyle(h) {
      if (this.isDone(h)) return { background: '#ffe0d9', color: '#7c1405', borderColor: '#ffc4b8' }
      return { background: 'transparent', color: 'rgba(32,30,29,0.6)' }
    },
    setsText(h) {
      const sets = this.setsOf(h)
      if (!sets.length) return 'подходов ещё нет'
      const remain = Math.max(0, this.planOf(h) - this.totalOf(h))
      const tail = remain > 0 ? `осталось ${remain}` : 'план закрыт'
      return `${sets.map(s => s.reps).join(' + ')} · ${tail}`
    },
    async addSet(h, reps) {
      this.busy = true
      try {
        await hbtAddLog(this, this.widget.param, h, reps)
        await this.reload()
        this.$root.$emit(HBT_REFRESH)
      } finally {
        this.busy = false
      }
    },
    async toggleCheck(h) {
      this.busy = true
      try {
        if (this.isDone(h)) {
          await hbtDeleteTodayLogs(this, this.widget.param, h.id, this.logs)
        } else {
          await hbtAddLog(this, this.widget.param, h, 1)
        }
        await this.reload()
        this.$root.$emit(HBT_REFRESH)
      } finally {
        this.busy = false
      }
    },
    async onPlan(h, event) {
      const plan = Math.max(0, parseInt(event.target.value, 10) || 0)
      if (plan === h.plan) return
      const saved = await hbtSavePlan(this, this.widget.param, h, plan)
      if (saved) {
        this.$set(h, 'plan', plan)
        useCubus.toastMessage(this, `${h.name}: план на день — ${plan}`)
        this.$root.$emit(HBT_REFRESH)
      }
    },
  },
}
</script>

<style src="./habits/habits.css"></style>
<style scoped>
.hbt-ex {
  padding: 16px 0;
  border-bottom: 1px solid rgba(32, 30, 29, 0.4);
}
.hbt-plan-input {
  width: 62px;
  font-size: 16px;
  text-align: right;
}
</style>
